import 'reflect-metadata';
import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router-dom';
import { Grid, Typography } from '@mui/material';
import { Card } from '@tcl-boron-prefabs/card';
import { Button } from '@tcl-boron-prefabs/button';
import { Warning, Information } from '@tcl-boron-icons/icons';
import { GrayscalePalette, WarningPalette } from '@tcl-boron-colors/colors';
import { MissingOrderItems, RequiredItem } from '../types';
import { ActionType, MISSING_ITEM_PATIENT_FIELDS, MISSING_ITEM_PROVIDER_FIELDS } from '../constants';
import { PATIENT_MOI_ALERT_CONFIG } from '../constants';
import usePortalContext from '../../../context/usePortalContext';
import { BillingInfo } from '../../payment-details/types';
import { FinancialAssistanceStatuses } from 'requisition-service-shared';
import logger from '../../../../../utils/logger';
import isPathAllowed from '../../../shared/utils/isPathAllowed';

const useStyles = createUseStyles({
  container: {
    marginBottom: '16px',
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    alignItems: 'center',
    marginTop: '0px',
  },
  itemContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '8px',
  },
  patientMOICard: {
    backgroundColor: 'rgba(255, 197, 108, 0.4)',
    padding: '24px',
  },
  providerMOICard: {
    backgroundColor: 'rgba(214, 255, 252, 1)',
    padding: '24px',
  },
  subtext: { marginBottom: '16px', marginTop: '16px' },
});

const PatientMissingItemsAlert: React.FC<{
  missingOrderItems: RequiredItem[];
  faStatus: FinancialAssistanceStatuses | undefined;
}> = ({ missingOrderItems, faStatus }) => {
  const { push } = useHistory();
  const classes = useStyles();

  return (
    <Grid className={classes.container}>
      <Card className={classes.patientMOICard}>
        <div data-testid="patient-moi-card">
          <div className={classes.headerContainer}>
            <Warning color={WarningPalette[700]} />
            <Typography variant="h1">Missing items from your order</Typography>
          </div>
          <Typography variant="body1" className={classes.subtext}>
            You have the following items that are delaying your test results.
          </Typography>
          <div className={classes.itemContainer}>
            {missingOrderItems.map((item) => {
              const missingItemConfig = PATIENT_MOI_ALERT_CONFIG[item.type];
              if (!missingItemConfig) {
                return null;
              }

              switch (missingItemConfig.actionType) {
                case ActionType.LINK:
                  return (
                    <Button
                      key={item.type}
                      style={{ height: '32px' }}
                      ariaLabel={missingItemConfig.buttonText}
                      buttonType="secondary"
                      onClick={async () => {
                        const url = await missingItemConfig.getPatientLink({ faStatus });
                        window.open(url, '_blank');
                      }}
                    >
                      {missingItemConfig.buttonText}
                    </Button>
                  );
                case ActionType.PUSH:
                  return (
                    <Button
                      key={item.type}
                      style={{ height: '32px' }}
                      ariaLabel={missingItemConfig.buttonText}
                      buttonType="secondary"
                      onClick={() => push(missingItemConfig.navigationPath)}
                    >
                      {missingItemConfig.buttonText}
                    </Button>
                  );
                default:
                  return null;
              }
            })}
          </div>
        </div>
      </Card>
    </Grid>
  );
};

const ProviderMissingItemsAlert: React.FC = () => {
  const classes = useStyles();
  return (
    <Grid className={classes.container}>
      <Card className={classes.providerMOICard}>
        <div data-testid="provider-moi-card">
          <div className={classes.headerContainer}>
            <Information color={GrayscalePalette[700]} />
            <Typography variant="h1">Missing items from your clinic</Typography>
          </div>
          <Typography variant="body1" className={classes.subtext}>
            We have reached out to your clinician&apos;s office to get the missing item(s).
          </Typography>
          <Typography variant="body1">No action is required from you at this time.</Typography>
        </div>
      </Card>
    </Grid>
  );
};

const MissingItemsAlert: React.FC<{ missingOrderItems: MissingOrderItems }> = ({ missingOrderItems }) => {
  const { patient, fetchHelper } = usePortalContext();
  const [loading, setLoading] = useState(false);
  const [faStatus, setFaStatus] = useState<FinancialAssistanceStatuses | undefined>(undefined);

  const fetchFAStatus = async () => {
    try {
      const response = await fetchHelper<BillingInfo>(`/billing-info/${patient.orderId}`);
      if (response.financialAssistanceStatus) {
        setFaStatus(response.financialAssistanceStatus);
      }
    } catch (e: any) {
      logger.error(e, 'Error fetching billing info');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    await fetchFAStatus();
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const patientMissingOrderItems = missingOrderItems.patient.filter((item) => {
    const moiAlertConfigItem = PATIENT_MOI_ALERT_CONFIG[item.type];
    if (!moiAlertConfigItem || !MISSING_ITEM_PATIENT_FIELDS.includes(item.type)) return false;

    if (moiAlertConfigItem.actionType === ActionType.PUSH) {
      return isPathAllowed(moiAlertConfigItem.navigationPath, patient.toggles);
    }
    return true;
  });

  const providerMissingOrderItems = missingOrderItems.provider.filter((item) =>
    MISSING_ITEM_PROVIDER_FIELDS.includes(item.type),
  );

  if (loading) {
    return null;
  } else if (patientMissingOrderItems.length) {
    return <PatientMissingItemsAlert missingOrderItems={patientMissingOrderItems} faStatus={faStatus} />;
  } else if (providerMissingOrderItems.length) {
    return <ProviderMissingItemsAlert />;
  } else {
    return null;
  }
};

export default MissingItemsAlert;
